/*
 * Copyright (c) Harness Inc.
 *
 * This source code is licensed under the Apache 2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { ImgHTMLAttributes, ReactElement } from 'react'
import css from './ThumbnailSelect.css'
import { Layout, LayoutProps } from '../../layouts/Layout'
import { Button } from '../Button/Button'
import { FormGroup } from '@blueprintjs/core'
import { connect, FormikContextType } from 'formik'
import { get, isEmpty } from 'lodash-es'
import { Intent } from '@harnessio/design-system'
import { IconName } from '@harnessio/icons'
import { errorCheck, WrapOptionalTooltip } from '../../core/Utils'
import { Thumbnail, ThumbnailProps } from '../Thumbnail/Thumbnail'
import { FormError } from '../FormError/FormError'
import { PopoverProps } from 'components/Popover/Popover'
import cx from 'classnames'

export interface Item {
  label: string | React.ReactElement
  icon?: IconName | React.ReactElement
  imageProps?: ImgHTMLAttributes<HTMLOrSVGImageElement>
  value: string
  disabled?: boolean
  tooltip?: ReactElement | string
  tooltipProps?: PopoverProps
}

export interface ConnectedThumbnailSelectProps extends ThumbnailSelectProps {
  formik: FormikContextType<Record<string, never>>
}

export interface ThumbnailSelectProps extends Pick<ThumbnailProps, 'size' | 'cornerSelected'> {
  name: string
  items: Item[]
  isReadonly?: boolean
  layoutProps?: Partial<LayoutProps>
  changeText?: string
  cancelText?: string
  className?: string
  thumbnailClassName?: string
  onChange?(val: string): void
  expandAllByDefault?: boolean
  staticItems?: boolean
}

const ThumbnailSelect: React.FC<ConnectedThumbnailSelectProps> = props => {
  const {
    name,
    formik,
    items,
    isReadonly = false,
    layoutProps,
    changeText = 'Change',
    cancelText = 'Close',
    className,
    thumbnailClassName,
    size,
    onChange,
    expandAllByDefault,
    staticItems = false,
    cornerSelected = true
  } = props
  const value = get(formik.values, name)

  const [showAllOptions, setShowAllOptions] = React.useState(!isEmpty(value) || expandAllByDefault)

  const hasError = errorCheck(name, formik)
  const intent = hasError ? Intent.DANGER : Intent.NONE
  const helperText = hasError ? <FormError name={name} errorMessage={get(formik?.errors, name)} /> : null

  React.useEffect(() => {
    setShowAllOptions(isEmpty(value) || expandAllByDefault)
  }, [value])

  const selectedItemIndex = value ? items.findIndex(item => item.value === value) : -1

  let visibleItems: Item[] = items

  if (!staticItems) {
    visibleItems =
      selectedItemIndex > -1
        ? [items[selectedItemIndex], ...items.slice(0, selectedItemIndex), ...items.slice(selectedItemIndex + 1)]
        : items

    if (!showAllOptions) {
      visibleItems = visibleItems.slice(0, 1)
    }
  }

  function handleChangeClick(): void {
    setShowAllOptions(true)
  }

  function handleCancelClick(): void {
    setShowAllOptions(false)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const { value } = e.target

    formik.setFieldValue(name, value)
    formik.setFieldTouched(name, true, false)

    onChange?.(value)
  }

  return (
    <FormGroup className={cx(css.mainContainer, className)} helperText={helperText} intent={intent}>
      <Layout.Horizontal spacing={'medium'} {...layoutProps}>
        {visibleItems.map(item => {
          return (
            <WrapOptionalTooltip key={item.value} tooltip={item.tooltip} tooltipProps={item.tooltipProps}>
              <Thumbnail
                size={size}
                name={name}
                key={item.value}
                label={item.label}
                value={item.value}
                icon={item.icon}
                imageProps={item.imageProps}
                disabled={item.disabled || isReadonly}
                selected={item.value === value}
                onClick={handleChange}
                className={thumbnailClassName}
                cornerSelected={cornerSelected}
              />
            </WrapOptionalTooltip>
          )
        })}
        {showAllOptions || staticItems ? null : (
          <Button
            className={css.changeButton}
            disabled={isReadonly}
            minimal
            icon="Edit"
            iconProps={{ size: 12 }}
            withoutCurrentColor={true}
            intent="primary"
            data-testid="thumbnail-select-change"
            onClick={handleChangeClick}
            text={changeText}
          />
        )}
        {showAllOptions && value && !staticItems ? (
          <Button
            className={css.changeButton}
            disabled={isReadonly}
            minimal
            icon="cross"
            iconProps={{ size: 12 }}
            withoutCurrentColor={true}
            intent="primary"
            data-testid="thumbnail-select-cancel"
            onClick={handleCancelClick}
            text={cancelText}
          />
        ) : null}
      </Layout.Horizontal>
    </FormGroup>
  )
}

export default connect<ThumbnailSelectProps>(ThumbnailSelect)
